/**
 * `mediaSession` host requests (docs/ops-protocol.md, "Now playing"):
 * MediaPlayer's now-playing info and remote commands over the browser's
 * Media Session API, which puts what plays on the lock screen and the
 * system's media controls and routes their buttons (and hardware media
 * keys) back to the page.
 *
 * - `update` (`metadata`, `playbackState`, `position`, `commands`, `skip`)
 *   sets what the session shows and which commands the app handles; the
 *   browser extrapolates the position at the rate, so the app sends it
 *   only when it changes (a play, a pause, a seek). Answers `{}`.
 * - `receive` answers the next command the viewer gave: `{ command }`, with
 *   `position` for `seekTo` (seconds) and `interval` for the skips (the
 *   offset the system asked for, else the app's preferred one). Commands
 *   that arrive with nobody receiving wait in a queue, as WebSocket
 *   messages do (src/webSocket.ts). Fails `unsupported: …` on a page with
 *   no Media Session API, which ends the app's listening.
 */

import type { MediaSessionCommand, MediaSessionRequestParams } from './protocol';

/** The part of `navigator.mediaSession` the requests use; tests substitute a fake. */
export interface MediaSessionHost {
  setMetadata(metadata: { title: string; artist: string; album: string; artwork: { src: string }[] } | null): void;
  setPlaybackState(state: 'none' | 'playing' | 'paused'): void;
  setPositionState(state: { duration: number; playbackRate: number; position: number } | null): void;
  /** Registers (or, with null, removes) the handler of a Media Session action; may throw for an action the browser lacks. */
  setActionHandler(action: MediaSessionAction, handler: ((details: MediaSessionActionDetails) => void) | null): void;
}

/** A command the viewer gave, as `receive` answers it. */
export interface ReceivedCommand {
  command: MediaSessionCommand;
  position?: number;
  interval?: number;
}

/** The Media Session action each command listens to. */
const ACTIONS: Record<MediaSessionCommand, MediaSessionAction> = {
  play: 'play',
  pause: 'pause',
  stop: 'stop',
  nextTrack: 'nexttrack',
  previousTrack: 'previoustrack',
  seekTo: 'seekto',
  skipForward: 'seekforward',
  skipBackward: 'seekbackward',
};

/** One page's media session: what it shows, the commands it listens to, and the ones received. */
export class MediaSessionBridge {
  private readonly registered = new Set<MediaSessionCommand>();
  private readonly queue: ReceivedCommand[] = [];
  private readonly waiting: ((command: ReceivedCommand) => void)[] = [];
  private skip: { forward?: number; backward?: number } = {};

  constructor(private readonly host: MediaSessionHost) {}

  perform(params: MediaSessionRequestParams): Promise<Record<string, unknown>> {
    switch (params.action) {
      case 'update':
        this.update(params);
        return Promise.resolve({});
      case 'receive':
        return this.receive();
      default:
        return Promise.reject(new Error(`unsupported: mediaSession action ${String((params as { action: unknown }).action)}`));
    }
  }

  private update(params: MediaSessionRequestParams): void {
    if (params.metadata !== undefined) {
      const metadata = params.metadata;
      this.host.setMetadata(
        metadata === null
          ? null
          : { title: metadata.title ?? '', artist: metadata.artist ?? '', album: metadata.album ?? '', artwork: metadata.artwork ? [{ src: metadata.artwork }] : [] },
      );
    }
    if (params.playbackState !== undefined) this.host.setPlaybackState(params.playbackState);
    if (params.position !== undefined) {
      const position = params.position;
      this.host.setPositionState(position === null ? null : { duration: position.duration, playbackRate: position.rate, position: position.position });
    }
    if (params.skip !== undefined) this.skip = params.skip;
    if (params.commands !== undefined) this.listen(new Set(params.commands));
  }

  /** Listens to exactly `wanted`: handlers for the new ones, none for the dropped ones. */
  private listen(wanted: Set<MediaSessionCommand>): void {
    for (const command of this.registered) {
      if (wanted.has(command)) continue;
      this.setHandler(command, null);
      this.registered.delete(command);
    }
    for (const command of wanted) {
      if (this.registered.has(command)) continue;
      this.setHandler(command, (details) => this.deliver(this.received(command, details)));
      this.registered.add(command);
    }
  }

  private setHandler(command: MediaSessionCommand, handler: ((details: MediaSessionActionDetails) => void) | null): void {
    try {
      this.host.setActionHandler(ACTIONS[command], handler);
    } catch {
      // An action this browser does not know (`seekto` on an older Safari): the button never shows.
    }
  }

  private received(command: MediaSessionCommand, details: MediaSessionActionDetails): ReceivedCommand {
    const received: ReceivedCommand = { command };
    if (command === 'seekTo' && typeof details.seekTime === 'number') received.position = details.seekTime;
    if (command === 'skipForward' || command === 'skipBackward') {
      const preferred = command === 'skipForward' ? this.skip.forward : this.skip.backward;
      const interval = typeof details.seekOffset === 'number' ? details.seekOffset : preferred;
      if (interval !== undefined) received.interval = interval;
    }
    return received;
  }

  private deliver(command: ReceivedCommand): void {
    const wake = this.waiting.shift();
    if (wake) wake(command);
    else this.queue.push(command);
  }

  private receive(): Promise<Record<string, unknown>> {
    const queued = this.queue.shift();
    if (queued) return Promise.resolve({ ...queued });
    return new Promise((resolve) => this.waiting.push((command) => resolve({ ...command })));
  }
}

export async function performMediaSession(params: MediaSessionRequestParams, bridge: MediaSessionBridge | undefined): Promise<Record<string, unknown>> {
  if (!bridge) {
    // No Media Session API: nothing to show, and no command will ever come.
    if (params.action === 'update') return {};
    throw new Error('unsupported: this browser has no Media Session');
  }
  return bridge.perform(params);
}

/** `navigator.mediaSession`, where the browser has it. */
export function browserMediaSessionHost(): MediaSessionHost | undefined {
  if (typeof navigator === 'undefined' || !('mediaSession' in navigator) || typeof MediaMetadata === 'undefined') return undefined;
  const session = navigator.mediaSession;
  return {
    setMetadata: (metadata) => {
      session.metadata = metadata === null ? null : new MediaMetadata(metadata);
    },
    setPlaybackState: (state) => {
      session.playbackState = state;
    },
    setPositionState: (state) => {
      if (typeof session.setPositionState !== 'function') return;
      if (state === null) session.setPositionState();
      // A position past the duration, or a duration that is not a finite number, throws.
      else if (Number.isFinite(state.duration) && state.duration >= 0) session.setPositionState({ ...state, position: Math.min(Math.max(state.position, 0), state.duration) });
    },
    setActionHandler: (action, handler) => session.setActionHandler(action, handler),
  };
}
